import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { Globals } from 'app/_globals';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { environment } from 'environments/environment';
import { SortEvent } from 'primeng/api';
import { forkJoin, interval, Subscription } from 'rxjs';
import { NotificationService } from 'app/_services/notification.service';
import { CompetencyService } from 'app/_services/competency.service';
import Swal from 'sweetalert2';
import { finalize } from 'rxjs/operators';
import { isWorkOrderClosable } from 'app/_utils/work-order-close.utils';
import { WorkOrderStateService } from 'app/_services/work-order-state.service';
import { WorkOrderState } from 'app/_models/work-order-state';
import { ACTION_REQUIRED_OPTION, WORK_ORDER_OPTION_BUTTONS, workOrderStatesForStatus, workOrderStatusButtons } from 'app/_utils/work-order-list-filters.utils';
import { FilterToggleOption } from 'app/components/filter-toggle-group/filter-toggle-group.component';

@Component({
  selector: 'app-work-orders-overview',
  templateUrl: './work-orders-overview.component.html',
})
export class WorkOrdersOverviewComponent implements OnInit, OnDestroy {
  public role: any;
  private routeRole: any;
  public isControlAdmin: boolean;

  // display
  public workOrders = [];
  private updateSubscription: Subscription;
  public isSiteOwner: boolean;
  public columnOptionsSidebarVisible: boolean;
  public allowAssetScheduling: boolean = false;
  public allowWorkOrderScheduling: boolean = false;
  public showRequestedByColumn: boolean = false;
  private actionRequiredFilter: boolean;

  public togglableColumns = [
    { displayName: 'Asset', default: true },
    { displayName: 'Created By', default: false },
    { displayName: 'Created Date', default: false },
    { displayName: 'Last Updated', default: true },
    { displayName: 'Status', default: true },
    { displayName: 'Competency', default: true },
    // site, site owner not added here
  ];

  // table options
  public tableOptions = {
    joinAssetAndSite: true,
    visibleColumns: [],
  }
  // PORTAL-6 quick-picks and asset search
  public optionButtons: FilterToggleOption[] = WORK_ORDER_OPTION_BUTTONS;
  public selectedOption = '';
  public statusButtons: FilterToggleOption[] = [];
  public selectedState = '';
  private assetFilter = '';

  // pagination
  public pageSizes: any = environment.pageSizes;
  public pagination: any;
  @ViewChild('paginator', { static: true }) paginator;

  // Sorting and Filtering
  public defaultSortField = 'lastModifiedDate';
  public defaultSortOrder = -1;
  private sortOrderToText = (sortOrder: number) => { return sortOrder === 1 ? 'asc' : 'desc'; }
  public selectedSort = [this.defaultSortField + ',' + this.sortOrderToText(this.defaultSortOrder)];

  // Multiple select
  public selectedAllWorkOrders = false;

  // Loading
  public loading: boolean = false;
  public refreshing: boolean = false;
  public hasLoadedWorkOrders: boolean = false;
  public loadError: boolean = false;
  public skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  private activeLoadRequestId = 0;
  private refreshPauseTimeout: any = null;
  private refreshPauseDurationMs = 60000;
  private refreshIntervalMs = 20000;
  private lastLoadStartedAt = 0;

  constructor(
    private workOrderService: SiteWorkOrdersService,
    private competencyService: CompetencyService,
    private router: Router,
    private route: ActivatedRoute,
    globals: Globals,
    private notificationService: NotificationService,
    private workOrderStateService: WorkOrderStateService,
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;
    this.isControlAdmin = this.role === 11;
    this.allowAssetScheduling = globals.siteOwnerToggles.allowAssetScheduling;
    this.allowWorkOrderScheduling = globals.siteOwnerToggles.allowWorkOrderScheduling;

    if (this.routeRole === 'admin' || this.routeRole === 'support') {
      this.allowAssetScheduling = true;
      this.allowWorkOrderScheduling = true;
      this.showRequestedByColumn = true;
    }

    if (this.allowAssetScheduling) {
      this.togglableColumns.push({ displayName: 'Exempt From Scheduling', default: true },);
    }

    this.isSiteOwner = (this.role === 3);

    if (!this.showRequestedByColumn) {
      this.showRequestedByColumn = !!globals.siteOwnerToggles.allowTocAcknowledgement;
    }

    this.statusButtons = workOrderStatusButtons({
      scheduling: this.allowWorkOrderScheduling,
      toc: this.showRequestedByColumn,
    });
  }

  ngOnInit(): void {
    this.setupColumnOptions();

    this.route.queryParams.subscribe(params => {
      if (params['actionRequiredView'] === 'true') {
        this.actionRequiredFilter = true;
        this.selectedOption = ACTION_REQUIRED_OPTION;
      }
      this.resetPagination(); // call service to populate table
    });

    if (environment.devModeDisableGlobalPolling) {
      return;
    }

    this.updateSubscription = interval(this.refreshIntervalMs).subscribe((val) => {
      // Nobody is looking at a hidden tab, so skip the tick; onVisibilityChange catches up.
      if (this.refreshPauseTimeout || document.hidden) {
        return;
      }

      this.getPaginatedWorkerOrders('refresh');
    });
  }

  // When the tab comes back, refresh straight away if a tick was skipped while it was hidden.
  @HostListener('document:visibilitychange')
  public onVisibilityChange() {
    if (document.hidden || environment.devModeDisableGlobalPolling || this.refreshPauseTimeout || !this.hasLoadedWorkOrders) {
      return;
    }

    if (Date.now() - this.lastLoadStartedAt >= this.refreshIntervalMs) {
      this.getPaginatedWorkerOrders('refresh');
    }
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }

    this.clearRefreshPause();
  }

  public getPaginatedWorkerOrders(mode: 'blocking' | 'refresh' = 'blocking') {
    const loadRequestId = this.startLoading(mode);
    const forDelegate = (this.routeRole === 'site-delegate');

    this.workOrderService.getAllWorkOrders(this.pagination, this.selectedSort, false, forDelegate, this.actionRequiredFilter, {
      asset: this.assetFilter,
      states: workOrderStatesForStatus(this.selectedState),
    })
    .pipe(finalize(() => this.finishLoading(loadRequestId)))
    .subscribe(
      (data: any) => {
        if (loadRequestId !== this.activeLoadRequestId) {
          return;
        }

        const nextWorkOrders = data.body;
        this.preserveDetailedCompetencies(nextWorkOrders);
        this.workOrders = nextWorkOrders;
        this.hasLoadedWorkOrders = true;
        this.loadError = false;

        this.workOrders.forEach(workOrder => {
          workOrder.state = this.workOrderStateService.setWorkOrderState(workOrder);
          workOrder.exemptFromAssetScheduling = workOrder.asset.exemptFromAssetScheduling || workOrder.exemptFromAssetScheduling;
          workOrder.closable = isWorkOrderClosable(workOrder);
          workOrder.showRequestedBy = workOrder.state === WorkOrderState.PendingXfer
            || workOrder.state === WorkOrderState.PendingReturn
            || workOrder.state === WorkOrderState.XferAcknowledged
            || workOrder.state === WorkOrderState.ReturnAcknowledged;
          workOrder.requestedByDisplay = workOrder.showRequestedBy && workOrder.ownerWorkerUser
            ? `${workOrder.ownerWorkerUser.firstName || ''} ${workOrder.ownerWorkerUser.lastName || ''}`.trim()
            : '';
        });
        this.syncSelectAllBoxState();
        this.pagination.totalRecords = Number(data.headers.get('X-Total-Count'));

        this.loadDetailedCompetencies(loadRequestId);
      }, (error) => {
        if (loadRequestId !== this.activeLoadRequestId) {
          return;
        }
        console.error(error);
        this.loadError = true;
        this.notificationService.notify('danger', 'Error retrieving work order list');
      }
    );
  }

  private loadDetailedCompetencies(loadRequestId: number) {
    if (!this.workOrders || this.workOrders.length === 0) {
      return;
    }

    const workOrderIds = this.workOrders.map((wo) => wo.id);

    this.competencyService.getWorkOrderCompetenciesBulk(workOrderIds)
      .subscribe(
        (groups) => {
          if (loadRequestId !== this.activeLoadRequestId) {
            return;
          }
          const lookup = {};
          groups.forEach((group) => {
            lookup[group.workOrderId] = group.requiredCompetencies;
          });

          this.workOrders.forEach((workOrder) => {
            workOrder.detailedCompetencies = lookup[workOrder.id] || [];
          });
        },
        (error) => {
          if (loadRequestId !== this.activeLoadRequestId) {
            return;
          }
          console.error('Failed to load detailed competencies for work orders', error);
          this.notificationService.notify('danger', 'Error retrieving work order competencies');
        }
      );
  }

  // Keep last known competency data on matching rows so refreshes and refresh errors
  // do not blank out competency cells while the next competency fetch is pending.
  private preserveDetailedCompetencies(nextWorkOrders: any[]) {
    const existingDetailedCompetenciesById = {};

    this.workOrders.forEach((workOrder) => {
      if (workOrder.detailedCompetencies !== undefined) {
        existingDetailedCompetenciesById[workOrder.id] = workOrder.detailedCompetencies;
      }
    });

    nextWorkOrders.forEach((workOrder) => {
      if (existingDetailedCompetenciesById[workOrder.id] !== undefined) {
        workOrder.detailedCompetencies = existingDetailedCompetenciesById[workOrder.id];
      }
    });
  }

  public async onRowSelect(event) {
    const selectedWorkOrder = event.data;
    const selectedSite = selectedWorkOrder.asset.site;

    this.router.navigate(['/' + this.routeRole + '/sites/' + selectedSite.id + '/work-orders/' + selectedWorkOrder.id]);
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getPaginatedWorkerOrders();
  }

  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
    this.getPaginatedWorkerOrders();
  }

  public resetColumnOptions() {
    this.tableOptions = {
      joinAssetAndSite: true,
      visibleColumns: this.togglableColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.workOrderOveriew', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.workOrderOveriew');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }

  public customSort(sort: SortEvent) {
    const sortDirection = this.sortOrderToText(sort.order);

    this.selectedSort = [sort.field + ',' + sortDirection];

    if (sort.field === 'status') {
      this.selectedSort = ['workOrderState,' + sortDirection];
    }

    this.goToFirstPage();

    this.getPaginatedWorkerOrders();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public selectAll(event) {
    if (this.isControlAdmin || this.loading || this.refreshing) {
      return;
    }

    const checked = event.target.checked;
    const closableWorkOrders = this.workOrders.filter(workOrder => workOrder.closable);

    closableWorkOrders.forEach(workOrder => {
      workOrder.selected = checked;
    });

    this.selectedAllWorkOrders = checked && closableWorkOrders.length > 0;
    this.pauseRefreshBrieflyForBulkAction();
  }

  public syncSelectAllBoxState() {
    if (this.isControlAdmin) {
      this.selectedAllWorkOrders = false;
      return;
    }

    const closableWorkOrders = this.workOrders.filter(workOrder => workOrder.closable);
    this.selectedAllWorkOrders = closableWorkOrders.length > 0 && closableWorkOrders.every(workOrder => workOrder.selected);
    this.pauseRefreshBrieflyForBulkAction();
  }

  public updateCloseButtonState(): boolean {
    if (this.isControlAdmin || this.loading || this.refreshing) {
      return false;
    }

    return this.selectedAllWorkOrders || this.workOrders.some(workOrder => workOrder.selected);
  }

  public closeSelectedWorkOrders() {
    if (this.isControlAdmin || this.loading || this.refreshing) {
      return;
    }

    this.confirmAndCloseWorkOrders(this.getSelectedClosableWorkOrderIds());
  }

  public closeSingleWorkOrder(workOrderId: number) {
    if (this.isControlAdmin || this.loading || this.refreshing) {
      return;
    }

    const workOrder = this.workOrders.find(existingWorkOrder => existingWorkOrder.id === workOrderId && existingWorkOrder.closable);

    if (!workOrder) {
      return;
    }

    this.confirmAndCloseWorkOrders([workOrder.id]);
  }

  private confirmAndCloseWorkOrders(workOrderIds: number[]) {
    const workOrderIdsToClose = workOrderIds.filter((workOrderId, index) => workOrderIds.indexOf(workOrderId) === index);

    if (workOrderIdsToClose.length === 0) {
      return;
    }

    const isBulkClose = workOrderIdsToClose.length > 1;
    const confirmTitle = isBulkClose ? 'Close Work Orders?' : 'Close Work Order?';
    const confirmText = isBulkClose
      ? `Are you sure you want to close these ${workOrderIdsToClose.length} work orders?`
      : 'Are you sure you want to close this work order?';
    const successMessage = isBulkClose ? 'Work orders have been closed.' : 'Work Order has been closed.';

    Swal.fire({
      title: confirmTitle,
      text: confirmText,
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes',
    }).then((result) => {
      if (result.value) {
        this.clearRefreshPause();
        const closeRequests = workOrderIdsToClose.map(idToClose =>
          this.workOrderService.closeWorkOrder(idToClose)
        );

        forkJoin(closeRequests).subscribe(
          (result: any) => {
            this.selectedAllWorkOrders = false;
            this.getPaginatedWorkerOrders('refresh');
            this.notificationService.notify('success', 'Closed!', successMessage);
          },
          (error) => {
            console.error(error);
            this.notificationService.notify('danger', `Error deleting the requirement`);
            throw error;
          }
        )
      }
    });
  }

  private getSelectedClosableWorkOrderIds(): number[] {
    return this.workOrders
      .filter(workOrder => workOrder.closable)
      .filter(workOrder => workOrder.selected)
      .map(workOrder => workOrder.id);
  }

  public async rejectWorkOrder(workOrder) {
    if (this.isControlAdmin || this.refreshing) {
      return;
    }

    const { value: comment } = await Swal.fire({
      titleText: 'Reason for rejection',
      input: 'textarea',
      inputValidator: (value) => {
        if (value.length === 0) {
          return 'You need to enter a comment when rejecting a work order'
        } else if (value.length > 255) {
          return 'Comments cannot be longer than 255 characters'
        }
      },
      inputAttributes: {
        'aria-label': 'Type your reason for rejecting the work order here'
      },
      showCancelButton: true,
    });

    if (comment) {
      const commentBody = {
        workOrder: workOrder.id,
        comment: comment,
      };

      const patch = {
        approvalStatus: 'REJECTED',
      };

      this.updateWorkOrder(workOrder, patch).subscribe(() => {
        this.workOrderService.addWorkOrderComment(commentBody).subscribe(() => {
          this.notificationService.notify('success', 'Updated!', 'Work Order has been updated.');
          this.resetPagination();
        });
      });
    }
  }

  private updateWorkOrder(workOrder: any, update: { approvalStatus?: string; released?: boolean; }) {
    const existingWorkOrder = {
      id: workOrder.id,
      released: workOrder.released,
      description: workOrder.description,
      approvalStatus: workOrder.approvalStatus,
      minimumPartySize: workOrder.minimumPartySize,
      scheduledStartTime: workOrder.scheduledStartTime,
      scheduledEndTime: workOrder.scheduledEndTime,
      checkOnSiteFormId: workOrder.checkOnSiteFormId,
      checkOffSiteFormId: workOrder.checkOffSiteFormId,
      exemptFromAssetScheduling: workOrder.exemptFromAssetScheduling,
    };

    const body = { ...existingWorkOrder, ...update };

    return this.workOrderService.updateWorkOrder(body);
  }

  public onOptionChanged(key: string) {
    this.selectedOption = key;
    this.actionRequiredFilter = key === ACTION_REQUIRED_OPTION;
    this.applyFilters();
  }

  public onStateChanged(key: string) {
    this.selectedState = key;
    this.applyFilters();
  }

  public onAssetFilterChanged(text: string) {
    this.assetFilter = text;
    this.applyFilters();
  }

  private applyFilters() {
    this.goToFirstPage();
    this.getPaginatedWorkerOrders();
    this.selectedAllWorkOrders = false;
  }

  private startLoading(mode: 'blocking' | 'refresh'): number {
    const requestId = ++this.activeLoadRequestId;
    this.lastLoadStartedAt = Date.now();
    if (mode === 'blocking') {
      this.clearRefreshPause();
    }

    this.loading = mode === 'blocking';
    this.refreshing = mode === 'refresh';

    return requestId;
  }

  private finishLoading(requestId: number) {
    if (requestId !== this.activeLoadRequestId) {
      return;
    }

    this.loading = false;
    this.refreshing = false;
  }

  // Pause the 20s auto-refresh while the user is building a bulk selection,
  // but only briefly so a forgotten checkbox does not leave the page stale.
  private pauseRefreshBrieflyForBulkAction() {
    const hasSelectedWorkOrders = this.workOrders.some(workOrder => workOrder.selected);

    if (!hasSelectedWorkOrders) {
      this.clearRefreshPause();
      return;
    }

    this.clearRefreshPause();
    this.refreshPauseTimeout = setTimeout(() => {
      this.refreshPauseTimeout = null;
    }, this.refreshPauseDurationMs);
  }

  private clearRefreshPause() {
    if (!this.refreshPauseTimeout) {
      return;
    }

    clearTimeout(this.refreshPauseTimeout);
    this.refreshPauseTimeout = null;
  }
}
